import React, { useState, useEffect } from 'react';
import confetti from 'canvas-confetti';
import photoPool from './photoPool.json';
import { getPlayerId } from './player';
import { supabase } from './supabaseClient';




const currentYear = 2026;

const getCurrentGameDay = () => {
  const now = new Date();
  
  // Convert to Eastern Time

  const isDST = (date) => {
    const jan = new Date(date.getFullYear(), 0, 1).getTimezoneOffset();
    const jul = new Date(date.getFullYear(), 6, 1).getTimezoneOffset();
    return Math.max(jan, jul) !== date.getTimezoneOffset();
  };
  
  const offset = isDST(now) ? -4 : -5; // Adjust for daylight saving time
  const etTime = new Date(now.getTime() + (offset * 60 * 60 * 1000));
  
  // If it's before 1 AM ET, use the previous day
  if (etTime.getHours() < 1) {
    etTime.setDate(etTime.getDate() - 1);
  }
  
  // Return date string in YYYY-MM-DD format
  return etTime.toISOString().split('T')[0];
};

// Add this function to select photos based on the day
// Returns true if the image URL loads and has real dimensions (not a tiny placeholder)
const validatePhoto = (url) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img.naturalWidth > 100 && img.naturalHeight > 100);
    img.onerror = () => resolve(false);
    img.src = url;
  });

// Gets up to maxCandidates photos starting from the same daily position,
// so we have backups to replace broken images without disrupting the cycle.
const getDailyPhotoCandidates = (pool, count = 5, maxCandidates = 20) => {
  const gameDay = getCurrentGameDay();
  let seed = 0;
  for (let i = 0; i < gameDay.length; i++) {
    seed += gameDay.charCodeAt(i);
  }
  const totalPhotos = pool.length;
  const daysPerCycle = Math.ceil(totalPhotos / count);
  const epochDay = Math.floor(new Date(gameDay).getTime() / (24 * 60 * 60 * 1000));
  const cycleNumber = Math.floor(epochDay / daysPerCycle);
  const dayInCycle = epochDay % daysPerCycle;

  const poolCopy = [...pool];
  let cycleSeed = seed + cycleNumber * 12345;
  for (let i = poolCopy.length - 1; i > 0; i--) {
    cycleSeed = (cycleSeed * 9301 + 49297) % 233280;
    const j = Math.floor((cycleSeed / 233280) * (i + 1));
    [poolCopy[i], poolCopy[j]] = [poolCopy[j], poolCopy[i]];
  }

  const startIndex = dayInCycle * count;
  return poolCopy.slice(startIndex, startIndex + maxCandidates);
};

// Validates each candidate photo in order, returning the first `needed` that load.
// Falls back to unvalidated photos if not enough valid ones are found.
const selectValidDailyPhotos = async (pool, needed = 5) => {
  const candidates = getDailyPhotoCandidates(pool, needed, needed * 4);
  const valid = [];
  for (const photo of candidates) {
    if (valid.length >= needed) break;
    const ok = await validatePhoto(photo.src);
    if (ok) valid.push(photo);
    else console.warn('Skipping broken image:', photo.src);
  }
  // Fill any remaining slots with unvalidated candidates as a last resort
  for (const photo of candidates) {
    if (valid.length >= needed) break;
    if (!valid.includes(photo)) valid.push(photo);
  }
  return valid;
};

function App() {
  const feedbackRef = React.useRef(null);
  const [photos, setPhotos] = useState([]);
  const [currentPhoto, setCurrentPhoto] = useState(0);
  const [guesses, setGuesses] = useState([]);
  const [sliderValue, setSliderValue] = useState(2000);
  const [showResults, setShowResults] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [lastGuess, setLastGuess] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = React.useRef(null);

  const [lifetimeStats, setLifetimeStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [isLoadingGameState, setIsLoadingGameState] = useState(true);


    // Step 12: create/get a persistent anonymous player id (stored in localStorage)
    const [playerId] = useState(() => getPlayerId());

    // TEMP sanity check (remove later)
    useEffect(() => {
      console.log("Player ID:", playerId);
    }, [playerId]);
  

  // Updated scoring system: max 100 points, no points if more than 15 years off
  const calculateScore = (guess, actual) => {
    const yearsOff = Math.abs(guess - actual);
    if (yearsOff > 15) return 0;
    return Math.max(0, 100 - (yearsOff * 6.25)); // Lose ~6.67 points per year off to reach 0 at 15 years
  };

  const fetchLifetimeStats = async () => {
    if (!playerId) return;
  
    setStatsLoading(true);
  
    // 1) All game runs (total_score per run)
    const { data: runs, error: runsError } = await supabase
      .from('game_runs')
      .select('total_score')
      .eq('player_id', playerId);
  
    if (runsError) {
      console.error('Error loading game_runs:', runsError);
      setStatsLoading(false);
      return;
    }
  
    // 2) All guesses (years_off per photo guess)
    const { data: guessRows, error: guessesError } = await supabase
      .from('photo_guesses')
      .select('years_off')
      .eq('player_id', playerId);
  
    if (guessesError) {
      console.error('Error loading photo_guesses:', guessesError);
      setStatsLoading(false);
      return;
    }
  
    // DEBUG: Log the raw data
    console.log('Raw guess rows:', guessRows);
    console.log('First guess years_off:', guessRows?.[0]?.years_off, 'Type:', typeof guessRows?.[0]?.years_off);
  
    const scores = (runs || []).map(r => Number(r.total_score || 0));
    const topScore = scores.length ? Math.max(...scores) : 0;
    const avgScore = scores.length ? (scores.reduce((a,b) => a+b, 0) / scores.length) : 0;
  
    const yearsOffArr = (guessRows || []).map(g => Number(g.years_off ?? 0));
    
    // DEBUG: Log the converted array
    console.log('Years off array:', yearsOffArr);
    
    const avgYearsOff = yearsOffArr.length
      ? (yearsOffArr.reduce((a,b) => a+b, 0) / yearsOffArr.length)
      : 0;
  
    const pigTurePerfects = yearsOffArr.filter(v => v === 0).length;
    
    // DEBUG: Log the count
    console.log('Pig-ture Perfects count:', pigTurePerfects);
    console.log('Average Years Off:', avgYearsOff);
  
    setLifetimeStats({
      topScore,
      pigTurePerfects,
      avgScore,
      avgYearsOff,
      gamesPlayed: scores.length,
    });
  
    setStatsLoading(false);
  };

  const checkTodaysGame = async () => {
    const validatedPhotos = await selectValidDailyPhotos(photoPool, 5);
    setPhotos(validatedPhotos);

    if (!playerId) {
      setIsLoadingGameState(false);
      return;
    }
    
    const gameDay = getCurrentGameDay();
    
    const { data, error } = await supabase
      .from('game_runs')
      .select('id')
      .eq('player_id', playerId)
      .eq('game_day', gameDay)
      .single();
    
    if (error && error.code !== 'PGRST116') {
      // PGRST116 means no rows found, which is fine
      console.error('Error checking today\'s game:', error);
      setIsLoadingGameState(false);
      return;
    }
    
    if (data) {
      // They already played today - fetch their guesses
      const { data: guessData, error: guessError } = await supabase
        .from('photo_guesses')
        .select('actual_year, guess_year, score')
        .eq('run_id', data.id)
        .order('photo_index');
      
      if (!guessError && guessData) {
        const formattedGuesses = guessData.map(g => ({
          guess: g.guess_year,
          actual: g.actual_year,
          score: g.score
        }));
        
        setGuesses(formattedGuesses);
        setShowResults(true);
      }
    }
    
    setIsLoadingGameState(false);
  };


  const saveGameToSupabase = async (finalGuesses) => {
    if (!playerId) return;
  
    // STEP 1: First, ensure the player exists in the players table
    const { error: playerError } = await supabase
      .from('players')
      .upsert(
        { 
          id: playerId,
          last_played: new Date().toISOString()
        },
        { 
          onConflict: 'id' // Don't error if player already exists, just update
        }
      );
  
    if (playerError) {
      console.error('Error upserting player:', playerError);
      return; // Stop if we can't create the player
    }
  
    // STEP 2: Now insert the game run
    const gameDay = getCurrentGameDay();
    const totalScore = finalGuesses.reduce((sum, g) => sum + (g.score || 0), 0);
    const totalYearsOff = finalGuesses.reduce((sum, g) => sum + Math.abs((g.guess ?? 0) - (g.actual ?? 0)), 0);
  
    const { data: runRow, error: runErr } = await supabase
      .from('game_runs')
      .insert([{
        player_id: playerId,
        game_day: gameDay,
        total_score: totalScore,
        total_years_off: totalYearsOff,
      }])
      .select('id')
      .single();
  
    if (runErr) {
      console.error('Error inserting game_runs:', runErr);
      return;
    }
  
    // STEP 3: Insert the photo guesses
    const inserts = finalGuesses.map((g, idx) => ({
      run_id: runRow.id,
      player_id: playerId,
      game_day: gameDay,
      photo_index: idx + 1,
      actual_year: g.actual,
      guess_year: g.guess,
      years_off: Math.abs(g.guess - g.actual),
      score: g.score,
    }));
  
    const { error: guessesErr } = await supabase
      .from('photo_guesses')
      .insert(inserts);
  
    if (guessesErr) console.error('Error inserting photo_guesses:', guessesErr);
  };
  
  


  useEffect(() => {
    if (showResults) fetchLifetimeStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResults]);

  useEffect(() => {
    checkTodaysGame();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);
  
  

  // Improved height management - more precise
  useEffect(() => {
    function sendHeight() {
      if (containerRef.current) {
        // Use the actual container height plus a small buffer
        const height = containerRef.current.offsetHeight + 10;
        window.parent.postMessage({ type: "setHeight", height }, "*");
      }
    }
    
    // Initial height
    sendHeight();
    
    // Send height after a short delay to ensure content is rendered
    const timeoutId = setTimeout(sendHeight, 150);
    
    // Setup ResizeObserver for content changes
    let resizeObserver;
    if (window.ResizeObserver) {
      resizeObserver = new ResizeObserver(sendHeight);
      if (containerRef.current) {
        resizeObserver.observe(containerRef.current);
      }
    }
    
    // Listen for window resize
    window.addEventListener("resize", sendHeight);
    
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener("resize", sendHeight);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [currentPhoto, showFeedback, showResults]);

  // Additional hook for state changes that affect layout
  useEffect(() => {
    if (containerRef.current) {
      const height = containerRef.current.offsetHeight + 10;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    }
  }, [lastGuess, currentPhoto, showFeedback, showResults]);

  // Effect for handling scrolling to top after state changes
  useEffect(() => {
    if (!showFeedback) {
      window.scrollTo(0, 0);
    }
  }, [currentPhoto, showResults, showFeedback]);
  
  // Effect specifically for feedback area scrolling
  useEffect(() => {
    if (showFeedback && feedbackRef.current) {
      const timeoutId = setTimeout(() => {
        feedbackRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
      return () => clearTimeout(timeoutId);
    }
  }, [showFeedback]);

  const handleSubmit = () => {
    const photo = photos[currentPhoto];
    const guessData = { 
      guess: sliderValue, 
      actual: photo.year,
      score: Math.round(calculateScore(sliderValue, photo.year)) // Round to nearest integer
    };
    setLastGuess(guessData);
    setShowFeedback(true);

    const yearsOff = Math.abs(sliderValue - photo.year);
    triggerConfetti(yearsOff);
    triggerVibration(yearsOff);
  };

  const triggerConfetti = (yearsOff) => {
    // Base confetti configuration
    const defaults = {
      origin: { y: 0.9 }, // Launch from bottom
      spread: 90,
      startVelocity: 30,
      gravity: 1.2,
      ticks: 60,
      shapes: ['square', 'circle'],
      scalar: 1.2,
    };

    if (yearsOff === 0) {
      // Perfect guess - special celebration with gold/festive colors
      const colors = ['#FFD700', '#FF4500', '#8BCBFF', '#FF1493', '#32CD32'];
      
      // Multiple bursts for perfect score
      for (let i = 0; i < 5; i++) {
        setTimeout(() => {
          confetti({
            ...defaults,
            particleCount: 100,
            origin: { x: 0.2 + (i * 0.15), y: 0.9 },
            colors: colors,
            zIndex: 9999,
          });
        }, i * 200);
      }
      
      // Fire confetti from both sides for perfect score
      setTimeout(() => {
        confetti({
          ...defaults,
          particleCount: 150,
          angle: 60,
          spread: 80,
          origin: { x: 0, y: 0.9 },
          colors: colors,
        });
        
        confetti({
          ...defaults,
          particleCount: 150,
          angle: 120,
          spread: 80,
          origin: { x: 1, y: 0.9 },
          colors: colors,
        });
      }, 1000);
    } else if (yearsOff <= 5) {
      // Close guess - green confetti
      confetti({
        ...defaults,
        particleCount: 100,
        colors: ['#34C759', '#4CD964', '#32D74B', '#30D158'],
      });
    } else if (yearsOff <= 10) {
      // Pretty good guess - yellow confetti
      confetti({
        ...defaults,
        particleCount: 75,
        colors: ['#FFCC00', '#FFD60A', '#FFCF40', '#FFDC62'],
      });
    } else if (yearsOff <= 15) {
      // Decent guess - blue confetti as requested
      confetti({
        ...defaults,
        particleCount: 50,
        colors: ['#8BCBFF', '#67B0FC', '#5AC8FA', '#48ADF0'],
      });
    }
  };

  const triggerVibration = (yearsOff) => {
    // Check if vibration API is supported
    if (!navigator.vibrate) return;
    
    if (yearsOff === 0) {
      // Perfect guess - celebratory pattern with multiple bursts
      navigator.vibrate([100, 50, 150, 50, 200, 50, 300, 100, 700]);
    } else if (yearsOff <= 5) {
      // Close guess - strong single vibration
      navigator.vibrate(200);
    } else if (yearsOff <= 10) {
      // Pretty good guess - medium vibration
      navigator.vibrate(150);
    } else if (yearsOff <= 15) {
      // Decent guess - light vibration
      navigator.vibrate(100);
    }
    // No vibration for >15 years off (matches no confetti)
  };

  const handleNextPhoto = async () => {
    const updatedGuesses = [...guesses, lastGuess];
    setGuesses(updatedGuesses);
    setShowFeedback(false);
  
    if (currentPhoto === photos.length - 1) {
      await saveGameToSupabase(updatedGuesses);
      setShowResults(true);
    } else {
      setCurrentPhoto(currentPhoto + 1);
      setSliderValue(2000);
    }
  };
  

  // Updated share text to match Wordle-style format
  const generateShareText = () => {
    const totalScore = guesses.reduce((sum, g) => sum + g.score, 0);
    const totalYearsOff = guesses.reduce((sum, g) => sum + Math.abs(g.guess - g.actual), 0);
    
    // First line: App name
    let shareText = 'Pig-ture This 📸\n';
    
    // Second line: Score summary
    shareText += `Total Score: ${totalScore} | Years Off: ${totalYearsOff}\n\n`;
    
    // Third line: Results as squares in a row
    let squaresRow = '';
    guesses.forEach((g, i) => {
      const yearsOff = Math.abs(g.guess - g.actual);
      let square = '⬛'; // Default black square for poor
      
      if (yearsOff === 0) {
        square = '🟩'; // Green square for perfect
      } else if (yearsOff <= 5) {
        square = '🟩'; // Green square for very close
      } else if (yearsOff <= 10) {
        square = '🟨'; // Yellow square for good
      } else if (yearsOff <= 15) {
        square = '🟧'; // Orange square for decent
      } else {
        square = '🟥'; // Red square for poor
      }
      
      squaresRow += square;
    });
    shareText += `${squaresRow}\n\n`;
    
    // Fourth section: Individual breakdowns
    guesses.forEach((g, i) => {
      const yearsOff = Math.abs(g.guess - g.actual);
      const direction = g.guess > g.actual ? '↑' : g.guess < g.actual ? '↓' : '';
      
      if (g.score === 100 && yearsOff === 0) {
        shareText += `Photo ${i + 1}: 🐷 PIG-TURE PERFECT!\n`;
      } else {
        shareText += `Photo ${i + 1}: ${g.score}pts (${direction}${yearsOff} yrs off)\n`;
      }
    });
    
    
    // Final line: Plain text with URL
    shareText += '\nPlay Here: https://oinkoinkgames.com/#pigture-this';
    
    return shareText;
  };

  const handleShare = async () => {
    const shareText = generateShareText();
  
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Pig-ture This Results',
          text: shareText
        });
      } catch (error) {
        console.error('Share failed:', error);
        copyToClipboard(shareText);
      }
    } else {
      copyToClipboard(shareText);
    }
  };

  const copyToClipboard = (text) => {
    if (document.hasFocus()) {
      navigator.clipboard.writeText(text)
        .then(() => {
          // Create and show a temporary "copied" toast
          const toast = document.createElement('div');
          toast.innerText = 'Results copied to clipboard!';
          toast.style.position = 'fixed';
          toast.style.bottom = '20px';
          toast.style.left = '50%';
          toast.style.transform = 'translateX(-50%)';
          toast.style.backgroundColor = '#8BCBFF';
          toast.style.color = '#000';
          toast.style.padding = '10px 20px';
          toast.style.borderRadius = '8px';
          toast.style.zIndex = '9999';
          toast.style.boxShadow = '0 2px 10px rgba(0,0,0,0.3)';
          toast.style.transition = 'opacity 0.3s ease-in-out';
          
          document.body.appendChild(toast);
          
          setTimeout(() => {
            toast.style.opacity = '0';
            setTimeout(() => {
              document.body.removeChild(toast);
            }, 300);
          }, 2000);
        })
        .catch(() => {
          alert('Failed to copy results. Please try again.');
        });
    } else {
      alert('Please click the window to copy results.');
    }
  };

  // Get score color based on years off
  const getScoreColor = (yearsOff) => {
    if (yearsOff === 0) return '#34C759'; // Perfect - Green
    if (yearsOff <= 5) return '#34C759'; // Very close - Green
    if (yearsOff <= 10) return '#FFCC00'; // Good - Yellow
    if (yearsOff <= 15) return '#FF9500'; // Decent - Orange
    return '#FF3B30'; // Poor - Red (but no points)
  };

  // Get direction arrow for years off
  const getYearsOffDirection = (guess, actual) => {
    if (guess > actual) return '↑'; // User guessed later than actual
    if (guess < actual) return '↓'; // User guessed earlier than actual
    return ''; // Perfect guess
  };

  const handleSliderChange = (e) => {
    setSliderValue(Number(e.target.value));
  };

  // For smoother sliding experience
  const handleSliderMouseDown = () => {
    setIsDragging(true);
  };

  const handleSliderMouseUp = () => {
    setIsDragging(false);
  };

  // Setup event listeners for slider
  useEffect(() => {
    document.addEventListener('mouseup', handleSliderMouseUp);
    document.addEventListener('touchend', handleSliderMouseUp);
    
    return () => {
      document.removeEventListener('mouseup', handleSliderMouseUp);
      document.removeEventListener('touchend', handleSliderMouseUp);
    };
  }, []);

  const totalScore = guesses.reduce((sum, g) => sum + g.score, 0);
  const totalYearsOff = guesses.reduce((sum, g) => sum + Math.abs(g.guess - g.actual), 0);
  const { majorTicks, minorTicks } = generateTickMarks();

  // Generate tick marks based on year range - improved design
  function generateTickMarks() {
    const majorTicks = [];
    const minorTicks = [];
    
    // Major ticks every 25 years
    for (let year = 1900; year <= currentYear; year += 25) {
      const position = ((year - 1900) / (currentYear - 1900)) * 100;
      majorTicks.push({ year, position });
    }
    
    // Minor ticks every 5 years (excluding years where major ticks exist)
    for (let year = 1905; year <= currentYear; year += 5) {
      if (year % 25 !== 0) {
        const position = ((year - 1900) / (currentYear - 1900)) * 100;
        minorTicks.push({ year, position });
      }
    }
    
    return { majorTicks, minorTicks };
  }

  if (isLoadingGameState) {
    return (
      <div ref={containerRef} style={{
        background: '#1A2433',
        color: '#fff',
        padding: '2rem',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <p style={{ fontSize: '1.2rem' }}>Loading...</p>
      </div>
    );
  }

  if (showResults) {
    return (
      <div 
        ref={containerRef}
        style={{ 
          background: '#1A2433', 
          color: '#fff', 
          padding: '2rem', 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '2rem'
        }}
      >
        <div style={{ 
          width: '100%',
          maxWidth: '800px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center'
        }}>
          <h1 style={{ fontSize: '3rem', color: '#E17D66', marginBottom: '1.5rem' }}>Final Results</h1>

          {/* Top results summary card */}
          <div style={{
            width: '100%',
            marginBottom: '2rem',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center'
          }}>

            <div style={{
              display: 'flex',
              justifyContent: 'space-around',
              width: '100%',
              marginBottom: '1.5rem'
            }}>
              <div style={{ textAlign: 'center' }}>
                <h2 style={{ fontSize: '1.2rem', margin: '0 0 0.5rem 0', color: '#E5C99F' }}>Total Score</h2>
                <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{totalScore}</p>
              </div>
              <div style={{ textAlign: 'center' }}>
                <h2 style={{ fontSize: '1.2rem', margin: '0 0 0.5rem 0', color: '#E5C99F' }}>Years Off</h2>
                <p style={{ fontSize: '2.5rem', fontWeight: 'bold', margin: 0 }}>{totalYearsOff}</p>
              </div>
            </div>
            
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginBottom: '1.5rem' 
            }}>
              {guesses.map((g, i) => {
                const yearsOff = Math.abs(g.guess - g.actual);
                let color = getScoreColor(yearsOff);
                
                return (
                  <div key={i} style={{ 
                    width: '2rem', 
                    height: '2rem', 
                    backgroundColor: color,
                    borderRadius: '4px',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 'bold',
                    color: yearsOff <= 10 ? '#000' : '#FFF'
                  }}>
                    {i + 1}
                  </div>
                );
              })}
            </div>
            
            <button 
              onClick={handleShare} 
              style={{
                ...buttonStyle,
                width: '100%',
                maxWidth: '300px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: '0.25rem' }}>
                <path 
                  fill="currentColor" 
                  d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92 1.61 0 2.92-1.31 2.92-2.92s-1.31-2.92-2.92-2.92z"
                />
              </svg>
              Share Results
            </button>
          </div>

                    {/* Lifetime Stats */}
                    <div style={{
            width: '100%',
            background: '#263246',
            borderRadius: '16px',
            padding: '1.25rem',
            marginBottom: '2rem',
            border: '2px solid #E5C99F'
          }}>
            <h3 style={{ marginTop: 0, color: '#E5C99F', marginBottom: '0.75rem' }}>
              Your Lifetime Stats
            </h3>

            {statsLoading && (
              <p style={{ margin: 0, color: '#ccc' }}>Loading stats…</p>
            )}

            {!statsLoading && lifetimeStats && (
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                gap: '1rem'
              }}>
                <div>
                  <p style={{ margin: 0, color: '#ccc', fontSize: '0.9rem' }}>Top Score</p>
                  <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 'bold' }}>
                    {Math.round(lifetimeStats.topScore)}
                  </p>
                </div>

                <div>
                  <p style={{ margin: 0, color: '#ccc', fontSize: '0.9rem' }}>Pig-ture Perfects</p>
                  <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 'bold' }}>
                    {lifetimeStats.pigTurePerfects}
                  </p>
                </div>

                <div>
  <p style={{ margin: 0, color: '#ccc', fontSize: '0.9rem' }}>Average Score</p>
  <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 'bold' }}>
    {Math.round(lifetimeStats.avgScore)}
  </p>
</div>

                <div>
  <p style={{ margin: 0, color: '#ccc', fontSize: '0.9rem' }}>Years Off Per Photo</p>
  <p style={{ margin: 0, fontSize: '1.6rem', fontWeight: 'bold' }}>
    {Math.round(lifetimeStats.avgYearsOff)}
  </p>
</div>
              </div>
            )}

            {!statsLoading && !lifetimeStats && (
              <p style={{ margin: 0, color: '#ccc' }}>
                No lifetime stats yet — play a game first!
              </p>
            )}
          </div>


          {/* Daily play message */}
          <div style={{
            background: '#263246',
            borderRadius: '16px',
            padding: '1.5rem',
            marginBottom: '2rem',
            textAlign: 'center',
            border: '2px solid #E17D66'
          }}>
            <h3 style={{ 
              color: '#E17D66', 
              margin: '0 0 0.75rem 0',
              fontSize: '1.3rem'
            }}>
              Come Back Tomorrow! 🐷
            </h3>
            <p style={{ 
              margin: 0, 
              color: '#E5C99F',
              fontSize: '1rem',
              lineHeight: '1.4'
            }}>
              New photos are available every day.<br/>
              Challenge yourself with a fresh set tomorrow!
            </p>
          </div>

          {/* Individual photo results */}
          <div style={{ width: '100%' }}>
            {guesses.map((g, i) => {
              const yearsOff = Math.abs(g.guess - g.actual);
              const scoreColor = getScoreColor(yearsOff);
              
              return (
                <div key={i} style={{ 
                  marginBottom: '2rem', 
                  background: '#263246', 
                  borderRadius: '16px',
                  padding: '1.5rem',
                  overflow: 'hidden'
                }}>
                 
                  
                  <div style={{ 
                    display: 'flex', 
                    flexDirection: 'row', 
                    gap: '1.5rem',
                    flexWrap: 'wrap'
                  }}>
                    <img
                      src={photos[i].src}
                      alt={`Daily challenge ${i + 1}`}
                      onError={(e) => { e.target.style.display = 'none'; }}
                      style={{
                        width: '180px',
                        height: '180px',
                        objectFit: 'cover',
                        borderRadius: '12px'
                      }}
                    />
                    
                    <div style={{ flex: 1, minWidth: '240px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
                        <div>
                          <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Actual Year</p>
                          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{g.actual}</p>
                        </div>
                        
                        <div>
                          <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Your Guess</p>
                          <p style={{ fontSize: '1.5rem', fontWeight: 'bold', margin: 0 }}>{g.guess}</p>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div>
                          <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Years Off</p>
                          <p style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: 'bold', 
                            margin: 0,
                            color: '#ccc',
                            display: 'flex',
                            alignItems: 'center',
                          }}>
                            {getYearsOffDirection(g.guess, g.actual)}{yearsOff}
                          </p>
                        </div>
                        
                        <div>
                          <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Points</p>
                          <p style={{ 
                            fontSize: '1.5rem', 
                            fontWeight: 'bold', 
                            margin: 0, 
                            color: scoreColor 
                          }}>
                            +{g.score}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          
          
        </div>
      </div>
    );
  }

  return (
    <div 
      ref={containerRef}
      style={{
        background: '#1A2433',
        padding: '2rem 1rem',
        color: '#fff',
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        flexDirection: 'column',
      }}
    >
      <div style={{
        width: '100%',
        maxWidth: '700px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center'
      }}>
        <h1 style={{ fontSize: '3rem', color: '#E17D66', marginTop: 0, marginBottom: '1rem' }}>Pig-ture This</h1>

        <div style={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'space-between', 
          marginBottom: '1rem', 
          fontSize: '1rem', 
          color: '#ccc'
        }}>
          <span>Photo {currentPhoto + 1} / {photos.length}</span>
          <span>Total Score: {guesses.reduce((sum, g) => sum + g.score, 0)}</span>
        </div>

        <img
          src={photos[currentPhoto].src}
          alt={`Daily challenge ${currentPhoto + 1}`}
          onError={(e) => { e.target.style.display = 'none'; console.warn('Image failed to load:', photos[currentPhoto].src); }}
          style={{
            width: '100%',
            maxHeight: '420px',
            borderRadius: '16px',
            objectFit: 'contain',
            marginBottom: '0.5rem',
          }}
        />

        {!showFeedback && (
          <p style={{
            fontSize: '1rem',
            color: '#ccc',
            textAlign: 'center',
            marginBottom: '0.5rem',
            maxWidth: '90%',
          }}>
            Guess the year this photo was taken.
          </p>
        )}

        {!showFeedback && (
          <>
            {/* Year display above slider */}
            <p style={{ 
              fontSize: '1.5rem', 
              fontWeight: 'bold', 
              marginBottom: '0.5rem',
              width: '100%',
              textAlign: 'center' 
            }}>
              Your Guess: {sliderValue}
            </p>
 
            <div style={{ width: '100%', position: 'relative', padding: '0 1rem', boxSizing: 'border-box', marginBottom: '2rem' }}>
            <style>
  {`
    .custom-slider::-webkit-slider-thumb {
      appearance: none;
      width: 40px;
      height: 40px;
      background: transparent;
      cursor: pointer;
      border: none;
      box-shadow: none;
    }
    .custom-slider::-moz-range-thumb {
      width: 40px;
      height: 40px;
      background: transparent;
      cursor: pointer;
      border: none;
      box-shadow: none;
    }
    .custom-slider::-ms-thumb {
      width: 40px;
      height: 40px;
      background: transparent;
      cursor: pointer;
      border: none;
      box-shadow: none;
    }
  `}
</style>
              <input
                type="range"
                className="custom-slider"
                min="1900"
                max={currentYear}
                step="1"
                value={sliderValue}
                onChange={handleSliderChange}
                onMouseDown={handleSliderMouseDown}
                onTouchStart={handleSliderMouseDown}
                style={{
                  width: '100%',
                  appearance: 'none',
                  height: '8px',
                  borderRadius: '4px',
                  background: sliderValue === 2000
                    ? '#333'
                    : `linear-gradient(to right, #E5C99F 0%, #E5C99F ${(sliderValue - 1900) / (currentYear - 1900) * 100}%, #333 ${(sliderValue - 1900) / (currentYear - 1900) * 100}%, #333 100%)`,
                  outline: 'none',
                  marginBottom: '1.5rem',
                  zIndex: 2,
                  cursor: isDragging ? 'grabbing' : 'grab',
                  transition: isDragging ? 'background 0.1s ease' : 'background 0.3s ease',
                }}
              />
              <div
                style={{
                  position: 'absolute',
                  left: `calc(1rem + ${(sliderValue - 1900) / (currentYear - 1900) * 100}% * (100% - 2rem) / 100%)`,
                  top: '-6px',
                  fontSize: '24px',
                  transform: 'translateX(-50%)',
                  pointerEvents: 'none',
                  zIndex: 3,
                  userSelect: 'none',
                }}
              >
                🐷
              </div>


              {/* Improved tick marks with bold end years */}
              <div style={{ 
                width: '100%', 
                position: 'relative',
                marginTop: '-8px',
                height: '20px'
              }}>
                {/* First year (1900) - bold */}
                <div>
                  <div 
                    style={{
                      position: 'absolute',
                      left: '0%',
                      height: '12px',
                      width: '2px',
                      backgroundColor: '#F4A5A6',
                      transform: 'translateX(-50%)'
                    }}
                  />
                  <span 
                    style={{
                      position: 'absolute',
                      left: '0%',
                      top: '16px',
                      transform: 'translateX(-50%)',
                      fontSize: '0.8rem',
                      color: '#F4A5A6',
                      fontWeight: 'bold'
                    }}
                  >
                    1900
                  </span>
                </div>

                {/* Last year (currentYear) - bold */}
                <div>
                  <div 
                    style={{
                      position: 'absolute',
                      left: '100%',
                      height: '12px',
                      width: '2px',
                      backgroundColor: '#F4A5A6',
                      transform: 'translateX(-50%)'
                    }}
                  />
                  <span 
                    style={{
                      position: 'absolute',
                      left: '100%',
                      top: '16px',
                      transform: 'translateX(-50%)',
                      fontSize: '0.8rem',
                      color: '#F4A5A6',
                      fontWeight: 'bold'
                    }}
                  >
                    {currentYear}
                  </span>
                </div>

                {/* Middle major ticks */}
                {/* Middle major ticks */}
{majorTicks.filter(tick => tick.year !== 1900 && tick.year !== currentYear && tick.year !== 2025).map((tick, index) => (
                  <div key={index}>
                    <div 
                      style={{
                        position: 'absolute',
                        left: `${tick.position}%`,
                        height: '12px',
                        width: '2px',
                        backgroundColor: '#F4A5A6',
                        transform: 'translateX(-50%)'
                      }}
                    />
                    <span 
                      style={{
                        position: 'absolute',
                        left: `${tick.position}%`,
                        top: '16px',
                        transform: 'translateX(-50%)',
                        fontSize: '0.8rem',
                        color: '#F4A5A6',
                      }}
                    >
                      {tick.year}
                    </span>
                  </div>
                ))}

                {/* Minor ticks */}
                {minorTicks.map((tick, index) => (
                  <div 
                    key={`minor-${index}`} 
                    style={{
                      position: 'absolute',
                      left: `${tick.position}%`,
                      height: '6px',
                      width: '1px',
                      backgroundColor: '#F4A5A6',
                      transform: 'translateX(-50%)'
                    }}
                  />
                ))}
              </div>
            </div>

            <button onClick={handleSubmit} style={buttonStyle}>Submit</button>
          </>
        )}

        {showFeedback && (
          <div 
          ref={feedbackRef}
          style={{
            background: '#263246',
            borderRadius: '16px',
            padding: '1.5rem',
            marginTop: '1rem',
            width: '100%',
            maxWidth: '500px',
          }}
        >
        
            {Math.abs(lastGuess.guess - lastGuess.actual) === 0 && (
              <div style={{ 
                textAlign: 'center', 
                marginBottom: '1rem',
                borderRadius: '8px',
                padding: '1rem'
              }}>
                <h2 style={{ 
                  color: '#D1942F', 
                  margin: '0 0 0.5rem 0',
                  fontSize: '1.8rem'
                }}>
                  PIG-TURE PERFECT!
                </h2>
                <p style={{ margin: 0, color: '#FFFFFF' }}>Wow! You guessed the exact year!</p>
              </div>
            )}
            
                        {/* Reorganized feedback display */}
                        <div style={{ 
              display: 'flex', 
              justifyContent: 'center',
              alignItems: 'center', 
              marginBottom: '1.5rem',
              flexDirection: 'column'
            }}>
              <div style={{ textAlign: 'center', marginBottom: '1rem' }}>
                <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Correct Year</p>
                <p style={{ fontSize: '2rem', fontWeight: 'bold', margin: 0 }}>{lastGuess.actual}</p>
              </div>
              
              <div style={{ 
                display: 'flex', 
                width: '100%',
                justifyContent: 'space-around',
              }}>
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Points</p>
                  <p style={{ 
                    
                    fontSize: '2rem', 
                    fontWeight: 'bold', 
                    margin: 0, 
                    color: getScoreColor(Math.abs(lastGuess.guess - lastGuess.actual))
                  }}>
                    +{lastGuess.score}
                  </p>
                </div>
                
                <div style={{ textAlign: 'center' }}>
                  <p style={{ fontSize: '0.9rem', margin: '0 0 0.25rem 0', color: '#E5C99F' }}>Years Off</p>
                  <p style={{ 
  fontSize: '2rem', 
  fontWeight: 'bold', 
  margin: 0,
  color: '#ccc',  // Neutral gray color
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
}}>
  {getYearsOffDirection(lastGuess.guess, lastGuess.actual)}{Math.abs(lastGuess.guess - lastGuess.actual)}
</p>

                </div>
              </div>
            </div>
            
            
            
            <button 
              onClick={handleNextPhoto} 
              style={{ 
                ...buttonStyle, 
                marginTop: '1.5rem', 
                width: '100%' 
              }}
            >
              {currentPhoto === photos.length - 1 ? 'View Results' : 'Next Photo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const buttonStyle = {
  backgroundColor: '#E17D66',
  color: '#000',
  border: 'none',
  padding: '0.75rem 2rem',
  fontSize: '1rem',
  fontWeight: 'bold',
  borderRadius: '12px',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
};

export default App;